/**
 * Merko Integrations — REST surface.
 *
 * Every endpoint goes through the shared `api` instance, so it carries the
 * existing `authorizationToken` custom-authorizer header and the existing
 * stage-specific base URL — which is exactly what the handoff requires, for
 * *every* endpoint including callback completion.
 *
 * Conventions enforced here so no call site has to remember them:
 *
 *   • No user/owner ID is ever sent. The authorizer resolves the owner.
 *   • A body documented as `{}` is sent as an empty JSON **object** — never
 *     `null`, never form data, never provider credentials.
 *   • `Content-Type: application/json` accompanies every JSON body.
 *   • Path IDs are URL-encoded.
 *   • Bodies are refused locally above the backend's 32,000-byte cap, so an
 *     oversized payload fails with something explicable.
 *
 * Nothing in this module accepts a Composio tool slug, provider URL, version
 * string, credential, account ID or owner ID, because no endpoint does.
 */

import { api } from "core/axios/axios";
import { IntegrationClientError } from "./errors";
import { MAX_BODY_BYTES } from "./types";
import type {
    ActionOperation,
    AppId,
    AttemptState,
    Catalog,
    CompleteResult,
    ConnectAttempt,
    DeleteProfileResult,
    DisconnectResult,
    ExternalAction,
    ImportJob,
    Preview,
    Profile,
    ProfileInput,
    Sources,
} from "./types";

/** JSON headers for a body-carrying request. */
const JSON_HEADERS = { "Content-Type": "application/json" } as const;

/** The empty object every `{}` endpoint expects. */
const EMPTY = {} as const;

/**
 * Opt a request out of the shared axios interceptor's automatic replay.
 *
 * `core/axios` re-sends any request that comes back 401/403, once, with a fresh
 * token. For most calls that is a convenience. For two it is a contract
 * violation: completing an attempt carries a **single-use** `session_uri`, and
 * `verification_required` / `account_mismatch` / `invalid_session` — which the
 * handoff says must never be retried or replayed blindly — arrive as exactly
 * those statuses. Marking the request as already retried keeps the interceptor
 * out of it, so the first answer is the answer.
 */
const NO_AUTO_REPLAY = { _retry: true } as Record<string, unknown>;

const id = (value: string): string => encodeURIComponent(value);

/**
 * Guard the backend's body cap before a request leaves the browser.
 *
 * The limit is on UTF-8 bytes, not characters, so it is measured as bytes —
 * a 16,000-character HTML body of multi-byte characters would otherwise sail
 * past a `length` check and fail opaquely at the gateway.
 */
function assertWithinBodyLimit(body: unknown): void {
    const json = JSON.stringify(body ?? {});
    const bytes = typeof TextEncoder !== "undefined"
        ? new TextEncoder().encode(json).length
        : Buffer.byteLength(json, "utf8");
    if (bytes > MAX_BODY_BYTES) {
        throw new IntegrationClientError(
            "body_too_large",
            `This is too large to send (${bytes.toLocaleString()} bytes; the limit is ${MAX_BODY_BYTES.toLocaleString()}).`,
        );
    }
}

// ─── catalog ───────────────────────────────────────────────────────────────

/** `GET /integrations` → the whole catalog. */
export async function fetchCatalog(): Promise<Catalog> {
    const { data } = await api.get<Catalog>("integrations");
    return data;
}

// ─── connection lifecycle ──────────────────────────────────────────────────

/**
 * `POST /integrations/{appId}/connect` → an attempt plus the hosted URL.
 *
 * Must follow a user gesture: the returned `redirect_url` has to be opened
 * through that same gesture or the browser will block it.
 */
export async function startConnect(appId: AppId): Promise<ConnectAttempt> {
    const { data } = await api.post<ConnectAttempt>(
        `integrations/${id(appId)}/connect`,
        EMPTY,
        { headers: JSON_HEADERS },
    );
    return data;
}

/** `GET /integration-connection-attempts/{attemptId}` — stored state only. */
export async function fetchAttempt(attemptId: string): Promise<AttemptState> {
    const { data } = await api.get<AttemptState>(
        `integration-connection-attempts/${id(attemptId)}`,
    );
    return data;
}

/**
 * `POST /integration-connection-attempts/{attemptId}/complete`
 *
 * OAuth sends `{session_uri}`; an API-key link and every reconciliation retry
 * send `{}`. The URI is single-use: after a transient failure that may already
 * have redeemed it, retry with `{}` for the same attempt instead of replaying
 * it. Passing no `sessionUri` is therefore the *correct* retry, not a bug.
 */
export async function completeAttempt(
    attemptId: string,
    sessionUri?: string,
): Promise<CompleteResult> {
    const body = sessionUri ? { session_uri: sessionUri } : EMPTY;
    assertWithinBodyLimit(body);
    const { data } = await api.post<CompleteResult>(
        `integration-connection-attempts/${id(attemptId)}/complete`,
        body,
        { headers: JSON_HEADERS, ...NO_AUTO_REPLAY },
    );
    return data;
}

/**
 * `DELETE /integrations/{appId}`
 *
 * Disables local use and the app's import profiles before the remote account is
 * deleted. Contacts, suppressions, job history and any native connection are
 * all retained.
 */
export async function disconnectApp(appId: AppId): Promise<DisconnectResult> {
    const { data } = await api.delete<DisconnectResult>(`integrations/${id(appId)}`);
    return data;
}

// ─── sources ───────────────────────────────────────────────────────────────

/** `GET /integrations/{appId}/sources` — only valid after connection. */
export async function fetchSources(appId: AppId): Promise<Sources> {
    const { data } = await api.get<Sources>(`integrations/${id(appId)}/sources`);
    return data;
}

// ─── import profiles ───────────────────────────────────────────────────────

/** `GET /integration-import-profiles` */
export async function fetchProfiles(): Promise<Profile[]> {
    const { data } = await api.get<{ profiles: Profile[] }>("integration-import-profiles");
    return data?.profiles ?? [];
}

/** `POST /integration-import-profiles` → 201 */
export async function createProfile(input: ProfileInput): Promise<Profile> {
    assertWithinBodyLimit(input);
    const { data } = await api.post<Profile>("integration-import-profiles", input, {
        headers: JSON_HEADERS,
    });
    return data;
}

/**
 * `PATCH /integration-import-profiles/{profileId}`
 *
 * A **full replacement**, despite the verb. Callers must pass a clean
 * `ProfileInput` — see `toProfileInput()` in `profile.ts`, which strips the
 * response-only fields the backend rejects.
 */
export async function updateProfile(profileId: string, input: ProfileInput): Promise<Profile> {
    assertWithinBodyLimit(input);
    const { data } = await api.patch<Profile>(
        `integration-import-profiles/${id(profileId)}`,
        input,
        { headers: JSON_HEADERS },
    );
    return data;
}

/** `DELETE /integration-import-profiles/{profileId}` — contacts are retained. */
export async function deleteProfile(profileId: string): Promise<DeleteProfileResult> {
    const { data } = await api.delete<DeleteProfileResult>(
        `integration-import-profiles/${id(profileId)}`,
    );
    return data;
}

/**
 * `POST /integration-import-profiles/{profileId}/preview`
 *
 * A sample of up to ten rows — not a count, and not a guarantee of
 * eligibility.
 */
export async function previewProfile(profileId: string): Promise<Preview> {
    const { data } = await api.post<Preview>(
        `integration-import-profiles/${id(profileId)}/preview`,
        EMPTY,
        { headers: JSON_HEADERS },
    );
    return data;
}

/**
 * `POST /integration-import-profiles/{profileId}/runs` → 202
 *
 * `202` means queued, not imported. The caller owns the `Idempotency-Key`
 * lifecycle (see `idempotency.ts`): the same key retries a lost response, a new
 * key starts a new run.
 */
export async function startRun(profileId: string, idempotencyKey: string): Promise<ImportJob> {
    const { data } = await api.post<ImportJob>(
        `integration-import-profiles/${id(profileId)}/runs`,
        EMPTY,
        { headers: { ...JSON_HEADERS, "Idempotency-Key": idempotencyKey } },
    );
    return data;
}

// ─── jobs ──────────────────────────────────────────────────────────────────

/** `GET /integration-import-jobs/{jobId}` — the source of truth for progress. */
export async function fetchJob(jobId: string): Promise<ImportJob> {
    const { data } = await api.get<ImportJob>(`integration-import-jobs/${id(jobId)}`);
    return data;
}

/**
 * `POST /integration-import-jobs/{jobId}/retry` → 202, a **new** job.
 *
 * Valid only for a terminal job; a running one returns `job_running`. Needs a
 * new `Idempotency-Key`, because it starts a new full scan.
 */
export async function retryJob(jobId: string, idempotencyKey: string): Promise<ImportJob> {
    const { data } = await api.post<ImportJob>(
        `integration-import-jobs/${id(jobId)}/retry`,
        EMPTY,
        { headers: { ...JSON_HEADERS, "Idempotency-Key": idempotencyKey } },
    );
    return data;
}

// ─── external writes ───────────────────────────────────────────────────────

/**
 * `POST /integrations/{appId}/actions/preview`
 *
 * The returned `fields` must be rendered exactly as given, as **text**, with
 * the `warning` and the ten-minute expiry.
 */
export async function previewAction(
    appId: AppId,
    operation: ActionOperation,
    fields: Record<string, string>,
): Promise<ExternalAction> {
    const body = { operation, fields };
    assertWithinBodyLimit(body);
    const { data } = await api.post<ExternalAction>(
        `integrations/${id(appId)}/actions/preview`,
        body,
        { headers: JSON_HEADERS },
    );
    return data;
}

/** `GET /integration-actions/{actionId}` */
export async function fetchAction(actionId: string): Promise<ExternalAction> {
    const { data } = await api.get<ExternalAction>(`integration-actions/${id(actionId)}`);
    return data;
}

/**
 * `POST /integration-actions/{actionId}/confirm` → 202
 *
 * Sends only the `preview_hash` taken from the preview — never recomputed. A
 * user-initiated REST action; the assistant cannot reach this route.
 */
export async function confirmAction(
    actionId: string,
    previewHash: string,
): Promise<ExternalAction> {
    const body = { preview_hash: previewHash };
    assertWithinBodyLimit(body);
    const { data } = await api.post<ExternalAction>(
        `integration-actions/${id(actionId)}/confirm`,
        body,
        // A write confirmation is never replayed on the client's own initiative.
        { headers: JSON_HEADERS, ...NO_AUTO_REPLAY },
    );
    return data;
}
